// src/components/common/utils/outstanding.js

// ---------- Helpers ----------
export const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const round2 = (v) => {
  const n = safeNum(v);
  return Math.round(n * 100) / 100;
};

/**
 * fmt - Format number as INR currency (without the "INR" text, just symbol + commas)
 * Example: fmt(1837500) => "₹18,37,500.00"
 */
export const fmt = (v) =>
  safeNum(v).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * getInvoiceAmounts(invoiceDoc)
 *
 * Normalises an invoice document into a consistent numeric shape.
 * Uses the new fields:
 *   - principal
 *   - tax
 *   - overdueInterest
 *   - overdueTax
 *   - principalReceived
 *   - taxReceived
 *   - overdueInterestReceived
 *   - overdueTaxReceived
 *
 * Falls back to 0 where fields are missing.
 */
export const getInvoiceAmounts = (inv) => {
  if (!inv || typeof inv !== 'object') {
    return {
      principal: 0,
      principalReceived: 0,
      tax: 0,
      taxReceived: 0,
      overdueInterest: 0,
      overdueInterestReceived: 0,
      overdueTax: 0,
      overdueTaxReceived: 0,
    };
  }

  const principal = safeNum(inv.principal);
  const principalReceived = safeNum(inv.principalReceived);

  const tax = safeNum(inv.tax);
  const taxReceived = safeNum(inv.taxReceived);

  const overdueInterest = safeNum(inv.overdueInterest);
  const overdueInterestReceived = safeNum(inv.overdueInterestReceived);

  const overdueTax = safeNum(inv.overdueTax);
  const overdueTaxReceived = safeNum(inv.overdueTaxReceived);

  return {
    principal,
    principalReceived,
    tax,
    taxReceived,
    overdueInterest,
    overdueInterestReceived,
    overdueTax,
    overdueTaxReceived,
  };
};

/**
 * invoiceOutstanding(invoiceDoc)
 *
 * Returns the total outstanding amount for a single invoice:
 *   (principal + tax + overdueInterest + overdueTax)
 * - (principalReceived + taxReceived + overdueInterestReceived + overdueTaxReceived)
 */
export const invoiceOutstanding = (inv) => {
  const {
    principal,
    principalReceived,
    tax,
    taxReceived,
    overdueInterest,
    overdueInterestReceived,
    overdueTax,
    overdueTaxReceived,
  } = getInvoiceAmounts(inv);

  const total = principal + tax + overdueInterest + overdueTax;
  const received =
    principalReceived +
    taxReceived +
    overdueInterestReceived +
    overdueTaxReceived;

  const out = round2(total - received);
  return out < 0 ? 0 : out;
};

/**
 * getTotalsForInvoices(invoices)
 *
 * Aggregates all key numbers for a list of invoices.
 *
 * Returns:
 *  {
 *    totalPrincipal,
 *    totalPrincipalReceived,
 *    totalTax,
 *    totalTaxReceived,
 *    totalOverdueInterest,
 *    totalOverdueInterestReceived,
 *    totalOverdueTax,
 *    totalOverdueTaxReceived,
 *
 *    totalOverdue,           // overdueInterest + overdueTax
 *    totalOverdueReceived,   // overdueInterestReceived + overdueTaxReceived
 *    totalOverdueOutstanding,
 *
 *    totalOutstanding,       // sum of invoiceOutstanding
 *
 *    countInvoices,
 *    countPaid,              // fully paid (outstanding ~ 0)
 *    countPartiallyPaid,     // received > 0 but outstanding > 0
 *    countUnpaid             // received == 0 and outstanding > 0
 *  }
 */
export const getTotalsForInvoices = (invoices) => {
  const list = Array.isArray(invoices) ? invoices : [];

  let totalPrincipal = 0;
  let totalPrincipalReceived = 0;
  let totalTax = 0;
  let totalTaxReceived = 0;
  let totalOverdueInterest = 0;
  let totalOverdueInterestReceived = 0;
  let totalOverdueTax = 0;
  let totalOverdueTaxReceived = 0;

  let totalOutstanding = 0;

  let countInvoices = 0;
  let countPaid = 0;
  let countPartiallyPaid = 0;
  let countUnpaid = 0;

  const EPS = 0.01;

  for (const inv of list) {
    countInvoices += 1;

    const {
      principal,
      principalReceived,
      tax,
      taxReceived,
      overdueInterest,
      overdueInterestReceived,
      overdueTax,
      overdueTaxReceived,
    } = getInvoiceAmounts(inv);

    totalPrincipal += principal;
    totalPrincipalReceived += principalReceived;

    totalTax += tax;
    totalTaxReceived += taxReceived;

    totalOverdueInterest += overdueInterest;
    totalOverdueInterestReceived += overdueInterestReceived;

    totalOverdueTax += overdueTax;
    totalOverdueTaxReceived += overdueTaxReceived;

    const invTotal = principal + tax + overdueInterest + overdueTax;
    const invReceived =
      principalReceived +
      taxReceived +
      overdueInterestReceived +
      overdueTaxReceived;

    const invOutstanding = invoiceOutstanding(inv);
    totalOutstanding += invOutstanding;

    if (invOutstanding <= EPS) {
      countPaid += 1;
    } else if (invReceived <= EPS) {
      countUnpaid += 1;
    } else {
      countPartiallyPaid += 1;
    }
  }

  const totalOverdue = totalOverdueInterest + totalOverdueTax;
  const totalOverdueReceived =
    totalOverdueInterestReceived + totalOverdueTaxReceived;
  const totalOverdueOutstanding =
    totalOverdue - totalOverdueReceived;

  return {
    totalPrincipal: round2(totalPrincipal),
    totalPrincipalReceived: round2(totalPrincipalReceived),

    totalTax: round2(totalTax),
    totalTaxReceived: round2(totalTaxReceived),

    totalOverdueInterest: round2(totalOverdueInterest),
    totalOverdueInterestReceived: round2(
      totalOverdueInterestReceived
    ),

    totalOverdueTax: round2(totalOverdueTax),
    totalOverdueTaxReceived: round2(totalOverdueTaxReceived),

    totalOverdue: round2(totalOverdue),
    totalOverdueReceived: round2(totalOverdueReceived),
    totalOverdueOutstanding:
      totalOverdueOutstanding < 0
        ? 0
        : round2(totalOverdueOutstanding),

    totalOutstanding: round2(totalOutstanding),

    countInvoices,
    countPaid,
    countPartiallyPaid,
    countUnpaid,
  };
};